// Rene hjelpere for kartet (ingen Google Maps-avhengighet, så de kan testes i jsdom).

// ── Buet rute ────────────────────────────────────────────────────────────────

// Punkter langs en kvadratisk bezier-kurve fra `from` til `to`. Kontrollpunktet ligger vinkelrett
// på midten av strekningen, `curvature` × avstanden unna, alltid på samme side (til venstre sett
// fra `from` mot `to`). Regnes i et plan der lengdegrader er skalert med cos(breddegrad), så
// buen ser lik ut uansett hvor på kloden den ligger. Endepunktene returneres uendret.
export function curvedPath(from, to, { segments = 40, curvature = 0.22 } = {}) {
  const n = Math.max(2, Math.floor(segments));
  const k = Math.cos(((from.lat + to.lat) / 2) * Math.PI / 180) || 1;
  const toXY = p => ({ x: (p.lng - from.lng) * k, y: p.lat - from.lat });
  const a = { x: 0, y: 0 };
  const b = toXY(to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  // Vinkelrett (90° mot venstre) på retningen fra a til b
  const ctrl = { x: (a.x + b.x) / 2 - dy * curvature, y: (a.y + b.y) / 2 + dx * curvature };

  const pts = [];
  for (let i = 0; i <= n; i++) {
    if (i === 0) { pts.push({ lat: from.lat, lng: from.lng }); continue; }
    if (i === n) { pts.push({ lat: to.lat, lng: to.lng }); continue; }
    const t = i / n;
    const u = 1 - t;
    const x = u * u * a.x + 2 * u * t * ctrl.x + t * t * b.x;
    const y = u * u * a.y + 2 * u * t * ctrl.y + t * t * b.y;
    pts.push({ lat: from.lat + y, lng: from.lng + x / k });
  }
  return pts;
}

// ── Prisbobler som ikke skal overlappe ───────────────────────────────────────

const TILE = 256;
export const BUBBLE_W = 64;   // omtrentlig bredde på en prisboble i px
export const BUBBLE_H = 30;   // omtrentlig høyde i px
const GAP = 4;

// Web Mercator-piksler ved gitt zoom
export function projectToPixels({ lat, lng }, zoom) {
  const scale = TILE * Math.pow(2, zoom);
  const sin = Math.min(0.9999, Math.max(-0.9999, Math.sin(lat * Math.PI / 180)));
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

// Gir en piksel-forskyvning { dx, dy } per id slik at bobler som ville overlappet på dette
// zoomnivået legges ved siden av hverandre (rader på maks `perRow`, oppover). Bobler som ikke
// overlapper noen får ingen forskyvning. Deterministisk: grupper sorteres på id.
// items: [{ id, lat, lng }] → Map<id, { dx, dy }> (bare ids som er flyttet)
export function spreadBubbles(items, zoom, { width = BUBBLE_W, height = BUBBLE_H, perRow = 3 } = {}) {
  const pts = items.map(it => ({ id: it.id, ...projectToPixels(it, zoom) }));
  const parent = pts.map((_, i) => i);
  const find = i => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      if (Math.abs(pts[i].x - pts[j].x) < width && Math.abs(pts[i].y - pts[j].y) < height) {
        parent[find(i)] = find(j);
      }
    }
  }
  const groups = new Map();
  pts.forEach((p, i) => {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(p);
  });

  const out = new Map();
  groups.forEach(members => {
    if (members.length < 2) return;
    members.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    const cols = Math.min(perRow, members.length);
    members.forEach((m, i) => {
      const row = Math.floor(i / cols);
      const inRow = Math.min(cols, members.length - row * cols);
      const col = i % cols;
      out.set(m.id, {
        dx: Math.round((col - (inRow - 1) / 2) * (width + GAP)),
        dy: -Math.round(row * (height + GAP)),
      });
    });
  });
  return out;
}
